import type { Cents, PaymentLineEconomics, PaymentLineEconomicsInput } from "./types.js";

function assertNonNegativeCents(name: string, value: Cents) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer number of cents`);
  }
}

// Resolves the financial facts shared by payment lines in sales and account
// collections. It deliberately does not decide a discount: sale and account
// contexts have different commercial eligibility rules, but both persist the
// resulting customer payment, discount, net receipt and commission alike.
export function computePaymentLineEconomics(
  input: PaymentLineEconomicsInput,
): PaymentLineEconomics {
  assertNonNegativeCents("customerPaidCents", input.customerPaidCents);
  assertNonNegativeCents("discountCents", input.discountCents);

  if (input.requiresNetReceived && input.settlesInBatch) {
    throw new RangeError("a payment method cannot require net received and settle in batch");
  }

  if (input.settlesInBatch && input.netReceivedCents !== undefined) {
    throw new RangeError("a batch-settled payment cannot include netReceivedCents");
  }

  if (!input.requiresNetReceived && input.netReceivedCents !== undefined) {
    throw new RangeError("netReceivedCents is only allowed for payment methods that require it");
  }

  if (input.netReceivedCents !== undefined) {
    assertNonNegativeCents("netReceivedCents", input.netReceivedCents);
  }

  const debtCancelledCents = input.customerPaidCents + input.discountCents;
  if (!Number.isSafeInteger(debtCancelledCents)) {
    throw new RangeError("debtCancelledCents exceeds the safe integer range");
  }

  if (input.settlesInBatch) {
    return {
      customerPaidCents: input.customerPaidCents,
      discountCents: input.discountCents,
      debtCancelledCents,
      netReceivedCents: null,
      commissionCents: 0,
      recognition: "batch",
    };
  }

  if (input.requiresNetReceived && input.netReceivedCents === undefined) {
    return {
      customerPaidCents: input.customerPaidCents,
      discountCents: input.discountCents,
      debtCancelledCents,
      netReceivedCents: null,
      commissionCents: 0,
      recognition: "pending_net",
    };
  }

  const netReceivedCents = input.requiresNetReceived
    ? input.netReceivedCents!
    : input.customerPaidCents;

  return {
    customerPaidCents: input.customerPaidCents,
    discountCents: input.discountCents,
    debtCancelledCents,
    netReceivedCents,
    commissionCents: Math.max(0, input.customerPaidCents - netReceivedCents),
    recognition: "immediate",
  };
}
